import { z } from 'zod';
import { GenerationModeSchema } from './credits.js';
import { ImageModelSchema } from './styles.js';
import type { StylePresetSchema } from './styles.js';
import { AmplifyRequestSchema, AmplifyResponseSchema } from './amplify.js';

export const GenerateRequestSchema = AmplifyRequestSchema.extend({
  // On a remix the sketch may be absent — we copy the source's amplified prompt
  // and skip Claude. The original creator's typed sketch is never exposed.
  sketch: z.string().max(600).default(''),
  mode: GenerationModeSchema.default('single'),
  /** Override the model picked by the style preset. */
  modelOverride: ImageModelSchema.nullable().default(null),
  /**
   * URL of a reference image — typically one of the user's owned NFTs. When
   * present, generation routes through FLUX Kontext (character-preserving
   * image-to-image) regardless of the style preset's default model.
   */
  referenceImageUrl: z.string().url().nullable().default(null),
  /**
   * When set, this generation is a remix of a public generation. The worker
   * copies the source's amplified prompt verbatim, skips Claude amplification,
   * runs image-gen against the requester's reference image, and bumps the
   * source's remix counter. The requester never sees the source creator's
   * original sketch — only the amplified prompt that did the work.
   */
  remixSourceId: z.string().nullable().default(null),
  /**
   * Fix mode: when set, the worker treats THIS URL as the image to edit and
   * `fixInstruction` as the user's specific change request. Claude writes a
   * surgical edit instruction (~100 words) that preserves the rest of the
   * image; gpt-image-1.5 edits the source via /v1/images/edits. Used after
   * a generation when the user wants to refine one image without starting
   * over.
   */
  fixSourceImageUrl: z.string().url().nullable().default(null),
  fixInstruction: z.string().max(500).nullable().default(null),
  /**
   * Optional reference images to composite INTO the fix — uploaded by the
   * user (logo, portrait, texture) and stored on R2 at upload-time. Up to
   * four references per fix. The amplifier sees them too (multimodal) so
   * the surgical edit prompt can name their contents explicitly.
   */
  fixReferenceImageUrls: z.array(z.string().url()).max(4).default([]),
  /**
   * Token id of the picked character (when referenceImageUrl came from the
   * whale picker). Lets the worker look up per-character TRAIT OVERRIDES —
   * operator-written authoritative trait facts for tokens whose art the
   * vision model chronically misreads (e.g. a white wrap that keeps being
   * rendered as a necklace). Null when no character or unknown.
   */
  characterTokenId: z.number().int().nullable().default(null),
});
export type GenerateRequest = z.infer<typeof GenerateRequestSchema>;

export const GeneratedImageSchema = z.object({
  url: z.string().url(),
  model: ImageModelSchema,
  /** Index within the response, 0-based. */
  idx: z.number().int().min(0),
});
export type GeneratedImage = z.infer<typeof GeneratedImageSchema>;

export const GenerationResultSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  request: GenerateRequestSchema,
  amplified: AmplifyResponseSchema,
  images: z.array(GeneratedImageSchema),
  creditsDebited: z.number().min(0),
  /** USDC cost we incurred. Tracked for ops, not shown to user. */
  costUsd: z.number().min(0),
  ownerAddress: z.string(),
  visibility: z.enum(['private', 'public']),
  /**
   * If this generation is a "fix" of another image, the parent's id. Used to
   * walk back to the original generation set, enforce the "first fix is free"
   * rule, and show provenance in the gallery.
   */
  parentGenerationId: z.string().nullable().default(null),
  /**
   * True once a free fix has been spawned from this generation set. Tracked
   * here on the parent so subsequent fixes (from any image in the set) know
   * to charge credits.
   */
  freeFixConsumed: z.boolean().default(false),
  /**
   * Optional user-facing note about a partial outcome — e.g. a battle-mode run
   * where one model failed to return an image (and its credit was refunded).
   * Absent on a clean run.
   */
  notice: z.string().nullable().default(null),
  /**
   * Which variant the owner chose as the feed cover / canonical preview. This
   * is the image shown in the feed AND the scene a remix derives from, so the
   * piece is consistent everywhere. Defaults to the first variant.
   */
  coverIndex: z.number().int().min(0).default(0),
});
export type GenerationResult = z.infer<typeof GenerationResultSchema>;

/**
 * Public feed / share-link card. Image-only by design — no sketch leak, no
 * amplified-prompt leak. The amplified prompt is only revealed AFTER a viewer
 * has run their own generation using this as a remix source; the source
 * creator's original sketch is never exposed to anyone but the creator.
 */
export interface GenerationCard {
  id: string;
  createdAt: string;
  ownerAddress: string;
  ownerDisplayName: string | null;
  stylePreset: z.infer<typeof StylePresetSchema>;
  aspectRatio: '1:1' | '3:2' | '2:3' | '16:9' | '9:16';
  /** First image URL — for thumbnail / hero. */
  thumbnail: string;
  imageCount: number;
  upvotes: number;
  remixes: number;
  /**
   * Whether the requesting wallet has already upvoted this piece. Set by the
   * feed endpoint when a session is present; undefined for anonymous reads.
   * Drives the upvote toggle's "voted" state across reloads.
   */
  viewerVoted?: boolean;
}
